import {
  Badge,
  Box,
  Button,
  Center,
  createStyles,
  Divider,
  Loader,
  Modal,
  ScrollArea,
  Text,
} from "@mantine/core";
import { useSelector } from "react-redux";
import { clearStediClaimStatus } from "../../store/reducers/billing";
import { useDispatch } from "react-redux";
import { IoCloseOutline } from "react-icons/io5";

const INK   = "#171717";
const MUTED = "#667085";
const LINE  = "#E5E5E5";

const STATUS_COLORS = {
  F0: { bg: "#F0FDF4", border: "#BBF7D0", text: "#15803D" },
  F1: { bg: "#F0FDF4", border: "#BBF7D0", text: "#15803D" },
  F2: { bg: "#F0FDF4", border: "#BBF7D0", text: "#15803D" },
  A1: { bg: "#EFF6FF", border: "#BFDBFE", text: "#1D4ED8" },
  A2: { bg: "#EFF6FF", border: "#BFDBFE", text: "#1D4ED8" },
  R0: { bg: "#FEF2F2", border: "#FECACA", text: "#DC2626" },
  R1: { bg: "#FEF2F2", border: "#FECACA", text: "#DC2626" },
  R2: { bg: "#FEF2F2", border: "#FECACA", text: "#DC2626" },
  E0: { bg: "#FFFBEB", border: "#FDE68A", text: "#B45309" },
  E1: { bg: "#FFFBEB", border: "#FDE68A", text: "#B45309" },
  default: { bg: "#F4F4F5", border: "#E4E4E7", text: "#52525B" },
};

const useStyles = createStyles(() => ({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px 16px",
    borderBottom: `1px solid ${LINE}`,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: INK,
    fontFamily: "Inter",
    margin: 0,
  },
  closeBtn: {
    background: "#F4F4F5",
    border: 0,
    borderRadius: 8,
    width: 34,
    height: 34,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    color: INK,
    flexShrink: 0,
  },
  card: {
    background: "#FAFAFA",
    border: `1px solid ${LINE}`,
    borderRadius: 10,
    padding: "16px 18px",
    marginBottom: 12,
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    color: MUTED,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
  },
  value: {
    fontSize: 13,
    fontWeight: 500,
    color: INK,
    fontFamily: "Inter",
    marginTop: 2,
  },
  row: {
    display: "flex",
    gap: 24,
    flexWrap: "wrap",
    marginTop: 12,
  },
  col: {
    minWidth: 120,
  },
  errorCard: {
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 10,
    padding: "14px 18px",
    marginBottom: 12,
  },
  errorText: {
    fontSize: 13,
    color: "#DC2626",
    fontFamily: "Inter",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 6,
    border: "1px solid",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "Inter",
  },
}));

function StatusBadge({ code, label }) {
  const palette = STATUS_COLORS[code] || STATUS_COLORS.default;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 10px",
        borderRadius: 6,
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        color: palette.text,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "Inter",
      }}
    >
      {code} — {label}
    </span>
  );
}

function formatServiceDate(raw) {
  if (!raw) return null;
  const fmt = (s) => {
    if (!s || s.length !== 8) return s;
    return `${s.slice(4, 6)}/${s.slice(6, 8)}/${s.slice(0, 4)}`;
  };
  return raw.split("-").map(fmt).join(" – ");
}

function formatAmountPaid(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  const num = parseFloat(raw);
  return isNaN(num) ? raw : `$${num.toFixed(2)}`;
}

function ClaimStatusCard({ result, classes }) {
  if (!result.success) {
    return (
      <div className={classes.errorCard}>
        <Text className={classes.label}>Claim ID: {result.claimmd_id || "—"}</Text>
        <Text className={classes.errorText} mt={4}>
          {result.errorMessage}
        </Text>
      </div>
    );
  }

  const stedi = result.stediResponse || {};
  const claims = stedi.claims || [];
  const subscriber = stedi.subscriber || {};
  const dependent = stedi.dependent || {};
  const subscriberName = [subscriber.firstName, subscriber.lastName].filter(Boolean).join(" ") || null;
  const patientName = [dependent.firstName, dependent.lastName].filter(Boolean).join(" ") || null;

  return (
    <div className={classes.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Text className={classes.label}>Claim ID: {result.claimmd_id || "—"}</Text>
        <Text className={classes.label}>{result.payer_name || result.payerid || "—"}</Text>
      </div>

      {claims.length === 0 && (
        <Text mt={8} fz={13} color={MUTED}>
          No claim status found in Stedi response.
        </Text>
      )}

      {claims.map((cs, i) => {
        const st = cs?.claimStatus || {};
        const serviceDate = formatServiceDate(st.claimServiceDate);
        const amountPaid = formatAmountPaid(st.amountPaid);

        return (
          <div key={i} style={{ marginTop: 12 }}>
            {i > 0 && <Divider mb={12} color={LINE} />}

            {/* Row 1: status fields */}
            <div className={classes.row}>
              {st.statusCategoryCode && (
                <div className={classes.col}>
                  <Text className={classes.label}>Category</Text>
                  <div style={{ marginTop: 4 }}>
                    <StatusBadge
                      code={st.statusCategoryCode}
                      label={st.statusCategoryCodeValue || st.statusCategoryCode}
                    />
                  </div>
                </div>
              )}
              {st.statusCode && (
                <div className={classes.col}>
                  <Text className={classes.label}>Status</Text>
                  <Text className={classes.value} mt={4}>
                    {st.statusCode} — {st.statusCodeValue || ""}
                  </Text>
                </div>
              )}
              {st.effectiveDate && (
                <div className={classes.col}>
                  <Text className={classes.label}>Effective Date</Text>
                  <Text className={classes.value}>{st.effectiveDate}</Text>
                </div>
              )}
              {st.trackingNumber && (
                <div className={classes.col} style={{ minWidth: 200 }}>
                  <Text className={classes.label}>Tracking #</Text>
                  <Text className={classes.value} style={{ wordBreak: "break-all" }}>
                    {st.trackingNumber}
                  </Text>
                </div>
              )}
            </div>

            {/* Row 2: billing fields */}
            {(serviceDate || amountPaid !== null) && (
              <div className={classes.row}>
                {serviceDate && (
                  <div className={classes.col} style={{ minWidth: 160 }}>
                    <Text className={classes.label}>Service Date</Text>
                    <Text className={classes.value}>{serviceDate}</Text>
                  </div>
                )}
                {amountPaid !== null && (
                  <div className={classes.col}>
                    <Text className={classes.label}>Amount Paid</Text>
                    <Text className={classes.value}>{amountPaid}</Text>
                  </div>
                )}
              </div>
            )}

            {/* Row 3: patient / subscriber */}
            {(patientName || subscriberName || subscriber.memberId) && (
              <div className={classes.row}>
                {patientName && (
                  <div className={classes.col}>
                    <Text className={classes.label}>Patient</Text>
                    <Text className={classes.value}>{patientName}</Text>
                  </div>
                )}
                {subscriberName && (
                  <div className={classes.col}>
                    <Text className={classes.label}>Subscriber</Text>
                    <Text className={classes.value}>{subscriberName}</Text>
                  </div>
                )}
                {subscriber.memberId && (
                  <div className={classes.col}>
                    <Text className={classes.label}>Member ID</Text>
                    <Text className={classes.value}>{subscriber.memberId}</Text>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const StediClaimStatusModal = ({ opened, onClose }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const stediClaimStatus = useSelector((s) => s.billing.stediClaimStatus);
  const loading = useSelector((s) => s.billing.stediClaimStatusLoading);

  const handleClose = () => {
    dispatch(clearStediClaimStatus());
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      withCloseButton={false}
      size="lg"
      padding={0}
      radius={12}
      zIndex={1005}
    >
      <div className={classes.header}>
        <p className={classes.title}>Stedi Claim Status</p>
        <button className={classes.closeBtn} onClick={handleClose}>
          <IoCloseOutline size={18} />
        </button>
      </div>

      <ScrollArea style={{ maxHeight: 520 }} p="md">
        {loading && (
          <Center py={48}>
            <Loader color="#FFBA45" size="md" />
          </Center>
        )}

        {!loading && !stediClaimStatus && (
          <Center py={48}>
            <Text fz={14} color={MUTED}>
              No status data available.
            </Text>
          </Center>
        )}

        {!loading && stediClaimStatus && (
          <Box px={4} pb={8}>
            {Array.isArray(stediClaimStatus) ? (
              stediClaimStatus.map((result, i) => (
                <ClaimStatusCard key={i} result={result} classes={classes} />
              ))
            ) : (
              <Text fz={14} color={MUTED}>
                Unexpected response format.
              </Text>
            )}
          </Box>
        )}
      </ScrollArea>

      <Box
        px={24}
        pb={20}
        pt={12}
        style={{ borderTop: `1px solid ${LINE}`, display: "flex", justifyContent: "flex-end" }}
      >
        <Button
          onClick={handleClose}
          variant="outline"
          color="gray"
          radius={8}
          fz={13}
          h={36}
        >
          Close
        </Button>
      </Box>
    </Modal>
  );
};

export default StediClaimStatusModal;
